/**
 * editorStatePersistence — bridges openFilesStore to the per-project cache
 * file (`.ateliercode/cache/editor-state.json`) so the tab strip survives
 * not just project switches (in-memory) but also app restarts (disk).
 *
 * The store keeps every project's tabs in memory across switches; this
 * module is what makes them stick across quits.
 */
import type { IBackend } from '@/services/backend/IBackend';
import { logger } from '@/lib/logger';
import {
  CacheKeys,
  envelope,
  readCache,
  writeCache,
  type CacheEnvelope,
} from '@/lib/projectCache';
import { useOpenFilesStore, type ProjectTabState } from '@/stores/openFilesStore';

const TAG = 'EditorState';
const SCHEMA_VERSION = 1;
const WRITE_DEBOUNCE_MS = 1000;

/** Per-project saved tab payload. */
type Payload = ProjectTabState;

const writeTimers = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * Read this project's cached tab state and hydrate the store with it.
 * Returns true on cache hit. Idempotent — safe to call repeatedly.
 *
 * `_backend` is reserved for a future variant that needs the backend to
 * validate cached paths before restoring (e.g. drop tabs whose files no
 * longer exist). Today we trust the cache and let the next read fail
 * naturally if a path is stale.
 */
export async function loadEditorStateForProject(
  _backend: IBackend,
  projectId: string,
): Promise<boolean> {
  try {
    const cached = await readCache<CacheEnvelope<Payload>>(
      projectId,
      CacheKeys.editorState,
    );
    if (!cached || cached.schemaVersion !== SCHEMA_VERSION) return false;
    useOpenFilesStore.getState().hydrateProject(projectId, cached.payload);
    logger.info(
      'FileTreePerf',
      `editor state from cache: ${cached.payload.open.length} tab(s) for project ${projectId}`,
    );
    return true;
  } catch (e) {
    logger.debug(TAG, `loadEditorStateForProject failed for ${projectId}:`, e);
    return false;
  }
}

/**
 * Schedule a debounced write of this project's current tab state to its
 * cache file. Multiple calls within WRITE_DEBOUNCE_MS coalesce into one
 * write — the user clicking through tabs doesn't pound the disk.
 */
export function scheduleEditorStateWrite(_backend: IBackend, projectId: string): void {
  const prev = writeTimers.get(projectId);
  if (prev) clearTimeout(prev);
  writeTimers.set(
    projectId,
    setTimeout(async () => {
      writeTimers.delete(projectId);
      const state = useOpenFilesStore.getState().byProject.get(projectId);
      if (!state) return;
      // Strip noise — `lastActiveAt` is regenerated on every interaction
      // anyway, and persisting it would just churn the cache file.
      const payload: Payload = {
        activeId: state.activeId,
        open: state.open.map((f) => ({
          id: f.id,
          path: f.path,
          name: f.name,
          dirty: false, // dirty state is in-memory only — we can't restore unsaved buffers
          lastActiveAt: f.lastActiveAt,
          scrollTop: f.scrollTop,
          cursorLine: f.cursorLine,
          cursorColumn: f.cursorColumn,
        })),
      };
      await writeCache(projectId, CacheKeys.editorState, envelope(payload, SCHEMA_VERSION));
    }, WRITE_DEBOUNCE_MS),
  );
}

/** Cancel any pending writes (e.g. on project close / app shutdown). */
export function cancelEditorStateWrites(projectId?: string): void {
  if (projectId === undefined) {
    for (const t of writeTimers.values()) clearTimeout(t);
    writeTimers.clear();
    return;
  }
  const t = writeTimers.get(projectId);
  if (t) {
    clearTimeout(t);
    writeTimers.delete(projectId);
  }
}
